import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  mvpApiGetJobTask,
  client,
  type JobTaskDetailSchema,
  type JobTaskSchema,
  type OoSParcelSchema,
} from "@augmodo/lebron-browser-client";
import {
  useFetchJobTasks,
  imageGalleryQueryOptions,
} from "@labeling-tools/api/lebron/react-query-hooks";
import {
  getNextPendingTaskData,
  resolveGalleryImageUrl,
} from "../utils/helpers";

const STALE_TIME = 30 * 1000;

type Args = {
  jobId: string | undefined;
  currentTaskId: string | undefined;
  enabled: boolean;
};

/**
 * Pick the task to prefetch next. Mirrors the server's next-pending claim,
 * which serves the next *unassigned* PENDING task: skip the current task, any
 * task already being labeled (claimed by this or another user), and any
 * assigned task. Without those guards a stale or multi-labeler list lets
 * "first PENDING" resolve to an already-claimed or already-seen task, so we'd
 * prefetch (and briefly flip to) the wrong task and then reconcile.
 *
 * `skipTaskIds` mirrors the server's propagation hold: tasks the current one's
 * labels are about to be propagated onto go last, unless nothing else is left.
 */
export function selectNextPrefetchTaskId(
  tasks: JobTaskSchema[] | undefined,
  currentTaskId: string | undefined,
  skipTaskIds: ReadonlySet<string> = new Set(),
): string | undefined {
  if (!tasks || !currentTaskId) return undefined;
  const candidates = tasks.filter(
    (t) =>
      t.status === "PENDING" &&
      t.id !== currentTaskId &&
      !t.actively_labeling &&
      !t.assigned_to,
  );
  const next =
    candidates.find((t) => !t.id || !skipTaskIds.has(t.id)) ?? candidates[0];
  return next?.id ?? undefined;
}

/**
 * Warms the react-query cache and the browser HTTP cache with the data needed
 * to render the *next* pending task in this job, so submission feels instant.
 *
 * Returns the prefetched task id and an isPrefetchReady flag — the submit
 * handler uses these to decide whether to optimistically flip the cache.
 */
export function useNextTaskPrefetch({ jobId, currentTaskId, enabled }: Args) {
  const { data: jobTasks } = useFetchJobTasks(
    enabled && jobId ? jobId : undefined,
  );

  const nextTaskId: string | undefined = useMemo(() => {
    if (!enabled) return undefined;
    return selectNextPrefetchTaskId(jobTasks?.tasks, currentTaskId);
  }, [enabled, jobTasks, currentTaskId]);

  const { data: nextTask } = useQuery<JobTaskDetailSchema>({
    queryKey: ["fetch-task", jobId, nextTaskId],
    queryFn: async () => {
      const r = await mvpApiGetJobTask({
        path: { job_id: jobId as string, task_id: nextTaskId as string },
      });
      if (r.error) throw new Error(String(r.error));
      return r.data as JobTaskDetailSchema;
    },
    enabled: !!enabled && !!jobId && !!nextTaskId,
    staleTime: STALE_TIME,
  });

  const nextParcelId = (nextTask?.data as any)?.parcel_id as string | undefined;

  const { data: nextParcel } = useQuery<OoSParcelSchema>({
    queryKey: ["parcel", nextParcelId],
    queryFn: async () => {
      const r = await client.get({ url: `/v1/oos-parcels/${nextParcelId}` });
      if (r.error) throw new Error(String(r.error));
      return r.data as OoSParcelSchema;
    },
    enabled: !!enabled && !!nextParcelId,
    staleTime: STALE_TIME,
  });

  const enriched = useMemo(() => {
    if (!nextParcel || !nextTask) return null;
    return getNextPendingTaskData(nextParcel, nextTask.data, nextTask);
  }, [nextParcel, nextTask]);

  const mainImageId = enriched?.image_id as string | undefined;
  const referenceImageId = (
    enriched?.data?.reference_images?.[
      enriched?.data?.reference_image_index
    ] as { image_id?: string } | undefined
  )?.image_id;

  const mainGalleryIds = mainImageId ? [mainImageId] : undefined;
  const referenceGalleryIds = referenceImageId ? [referenceImageId] : undefined;

  const mainGalleryOptions = imageGalleryQueryOptions(mainGalleryIds);
  const referenceGalleryOptions = imageGalleryQueryOptions(referenceGalleryIds);

  const { data: mainGallery } = useQuery({
    ...mainGalleryOptions,
    enabled: !!enabled && (mainGalleryOptions.enabled ?? false),
  });
  const { data: referenceGallery } = useQuery({
    ...referenceGalleryOptions,
    enabled: !!enabled && (referenceGalleryOptions.enabled ?? false),
  });

  // Pre-warm the browser HTTP cache with the raw GCS images that useImageCrop
  // will load and crop client-side via canvas. crossOrigin is required so the
  // cached response can be used by the canvas drawImage call.
  const [mainJpegLoaded, setMainJpegLoaded] = useState(false);
  const [referenceJpegLoaded, setReferenceJpegLoaded] = useState(false);

  useEffect(() => {
    setMainJpegLoaded(false);
    const mainUrl = resolveGalleryImageUrl(mainGallery?.images?.[0]);
    if (!enabled || !mainUrl) return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!cancelled) setMainJpegLoaded(true);
    };
    img.onerror = () => {};
    img.src = mainUrl;
    return () => {
      cancelled = true;
    };
  }, [enabled, mainGallery]);

  useEffect(() => {
    setReferenceJpegLoaded(false);
    const refUrl = resolveGalleryImageUrl(referenceGallery?.images?.[0]);
    if (!enabled || !refUrl) return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!cancelled) setReferenceJpegLoaded(true);
    };
    img.onerror = () => {};
    img.src = refUrl;
    return () => {
      cancelled = true;
    };
  }, [enabled, referenceGallery]);

  // Ready means: task detail, parcel, main gallery + JPEG bytes warm in cache,
  // and — only when the task has a reference image — reference gallery + JPEG
  // bytes warm too. Tasks without a reference image skip those checks so
  // isPrefetchReady isn't stuck false for them.
  const hasReferenceImage = !!referenceGalleryIds;
  const isPrefetchReady = !!(
    enabled &&
    nextTaskId &&
    nextTask &&
    nextParcel &&
    mainGallery &&
    mainJpegLoaded &&
    (!hasReferenceImage || (referenceGallery && referenceJpegLoaded))
  );

  return { nextTaskId, isPrefetchReady };
}
